import { useFetchOpenVrdhSlots } from '@/api/hooks/useFetchOpenVrdhSlots'
import type { VrdhSchedulingSlot } from '@/api/types/ProviderApi'
import { SlotDateButton } from '@/components/ScheduleCallPage/SlotDateButton'
import { SlotTimeButton } from '@/components/ScheduleCallPage/SlotTimeButton'
import AppointmentSquiggly from '@/components/svgs/Squiggly'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { Skeleton } from '@/ui/core/skeleton'
import { HStack, VStack } from '@/ui/core/stack'
import { cn } from '@/ui/utils'
import { isEmpty } from 'ramda'
import { useMemo, useState } from 'react'
import { FlatList } from 'react-native-gesture-handler'

export type SchedulingSlotsProps = {
	onSlotChosen: (slot: VrdhSchedulingSlot) => void
}

export const SchedulingSlots = ({ onSlotChosen }: SchedulingSlotsProps) => {
	const { isLoadingSlots, slotsByDate, chunkedSlotsByDate } = useFetchOpenVrdhSlots()
	const [selectedSlotDate, setSelectedSlotDate] = useState<string | null>()
	const [selectedSlot, setSelectedSlot] = useState<VrdhSchedulingSlot | null>()
	const [slotDateChunkIndex, setSlotDateChunkIndex] = useState(0)

	const hasMoreSlotDates = useMemo(() => {
		if (isEmpty(chunkedSlotsByDate)) {
			return false
		}

		return chunkedSlotsByDate.at(slotDateChunkIndex + 1) !== undefined
	}, [slotDateChunkIndex, chunkedSlotsByDate])

	const availableSlotDates = useMemo(() => {
		if (isEmpty(chunkedSlotsByDate)) {
			return false
		}

		return chunkedSlotsByDate.at(slotDateChunkIndex) ?? []
	}, [slotDateChunkIndex, chunkedSlotsByDate])

	const onNextDateChunkClicked = () => {
		setSlotDateChunkIndex((slotDateChunkIndex) => slotDateChunkIndex + 1)
		setSelectedSlotDate(null)
	}

	const onPrevDateChunkClicked = () => {
		setSlotDateChunkIndex((slotDateChunkIndex) => slotDateChunkIndex - 1)
		setSelectedSlotDate(null)
	}

	const onSlotDatePressed = (slotDate: string) => {
		setSelectedSlotDate(slotDate)
	}

	const onSlotTimePressed = (slot: VrdhSchedulingSlot) => {
		setSelectedSlot(slot)
		onSlotChosen(slot)
	}

	return (
		<VStack gap={36} className="h-full pl-16 pr-12 items-center flex-1">
			<Header variant="h2" className="text-center">
				Let's find a time that works for you.
			</Header>
			<Skeleton isLoading={isLoadingSlots} className={'h-24 w-full max-w-md flex'}>
				<VStack gap={8} className="min-w-80 items-center">
					<HStack className="justify-between">
						<Button
							variant="ghostBorderless"
							className={cn('px-0', slotDateChunkIndex <= 0 ? 'invisible' : '')}
							onPress={onPrevDateChunkClicked}
						>
							<ButtonText>Previous</ButtonText>
						</Button>
						{hasMoreSlotDates && (
							<Button
								variant="ghostBorderless"
								className="px-0"
								onPress={onNextDateChunkClicked}
							>
								<ButtonText>Next</ButtonText>
							</Button>
						)}
					</HStack>
					<VStack className="items-center justify-center" gap={24}>
						<HStack className="w-full justify-center">
							{Object.keys(availableSlotDates).map((slotDate, idx) => (
								<SlotDateButton
									key={slotDate}
									slotDate={slotDate}
									onPressed={onSlotDatePressed}
									isSelected={selectedSlotDate === slotDate}
									isFirst={idx === 0}
									isLast={idx === Object.keys(availableSlotDates).length - 1}
								/>
							))}
						</HStack>
						<HStack className="items-center justify-center">
							{selectedSlotDate && (
								<FlatList
									data={slotsByDate[selectedSlotDate]}
									numColumns={3}
									keyExtractor={(item) => `${item.startTimeUtc}-${item.vrdhId}`}
									renderItem={({ item }) => (
										<VStack className="items-center">
											<SlotTimeButton slot={item} onPressed={onSlotTimePressed} />
											{item === selectedSlot && <AppointmentSquiggly />}
										</VStack>
									)}
									contentContainerClassName="gap-2"
									columnWrapperStyle={{
										height: 42,
									}}
								/>
							)}
						</HStack>
					</VStack>
				</VStack>
			</Skeleton>
		</VStack>
	)
}
